import type { TenantWalletLedgerEntry } from '../schemas/tenant-wallet.js';

/**
 * Sprint 98 — Pure tenant-wallet balance math.
 *
 * Mirrors the pattern used by `computePayoutBatch` in
 * `agents/payouts.ts` — given one tenant's wallet-ledger entries,
 * compute the operator-visible balance and the next-payout-eligible
 * subset.
 *
 * No I/O. Shared by:
 *   - the dashboard `/billing` Balance card (current pending + lifetime
 *     stats)
 *   - the `runPayoutBatch` Cloud Function when `kind === 'tenant'`
 *     (eligibility filter + grand total)
 *   - a future customer-pwa "operator transparency" page (not yet built)
 *
 * `pending` = sum of entries that have NOT been consumed by a payout
 * (i.e. `payoutId == null`). This is the operator-visible "ready to
 * withdraw" balance. Negative entries (psp_fee, refund) subtract from
 * it as expected.
 *
 * `lifetimeCollected` and `lifetimeFees` are running totals across all
 * time, used by the Balance card's "since signup" header.
 */

export interface TenantWalletSummary {
  /** Operator-visible "ready to withdraw" amount, sen. Never negative. */
  readonly pendingSen: number;
  /** Same as `pendingSen` but BEFORE applying any pending withdraws —
   *  used in the "Request payout" preview so the operator sees what
   *  the next batch would actually pay. */
  readonly pendingExcludingWithdrawsSen: number;
  /** Sum of entries with `payoutId` set — i.e. money already disbursed. */
  readonly lifetimePaidOutSen: number;
  /** Lifetime gross collected (positive `collected` entries only). */
  readonly lifetimeCollectedSen: number;
  /** Lifetime PSP fees absorbed (positive number — sum of |psp_fee|). */
  readonly lifetimeFeesSen: number;
  /** Lifetime refunds (positive number — sum of |refund|). */
  readonly lifetimeRefundsSen: number;
  /** Count of pending entries — drives "X washes pending payout" copy. */
  readonly pendingCount: number;
}

export function computeTenantWalletSummary(
  entries: readonly TenantWalletLedgerEntry[],
): TenantWalletSummary {
  let pendingSen = 0;
  let pendingExcludingWithdrawsSen = 0;
  let lifetimePaidOutSen = 0;
  let lifetimeCollectedSen = 0;
  let lifetimeFeesSen = 0;
  let lifetimeRefundsSen = 0;
  let pendingCount = 0;

  for (const e of entries) {
    if (e.payoutId) {
      // Already consumed by a payout — only contributes to lifetimePaidOut.
      if (e.kind === 'withdraw') {
        lifetimePaidOutSen += Math.abs(e.amount);
      }
      continue;
    }
    // payoutId is null — this entry is pending.
    pendingSen += e.amount;
    if (e.kind !== 'withdraw') {
      pendingExcludingWithdrawsSen += e.amount;
    }
    pendingCount += 1;

    if (e.kind === 'collected') lifetimeCollectedSen += e.amount;
    if (e.kind === 'psp_fee') lifetimeFeesSen += Math.abs(e.amount);
    if (e.kind === 'refund') lifetimeRefundsSen += Math.abs(e.amount);
  }

  return {
    pendingSen: Math.max(0, pendingSen),
    pendingExcludingWithdrawsSen: Math.max(0, pendingExcludingWithdrawsSen),
    lifetimePaidOutSen,
    lifetimeCollectedSen,
    lifetimeFeesSen,
    lifetimeRefundsSen,
    pendingCount,
  };
}

/**
 * Convenience predicate — "does this tenant currently have anything to
 * pay out?" Used to enable/disable the Request-payout button and to
 * skip tenants in the daily auto-withdraw sweep.
 *
 * Threshold defaults to 0 (any positive pending qualifies) — caller
 * passes the tenant's configured threshold to enforce it.
 */
export function isWithdrawable(summary: TenantWalletSummary, thresholdSen = 0): boolean {
  return (
    summary.pendingExcludingWithdrawsSen >= Math.max(0, thresholdSen) &&
    summary.pendingExcludingWithdrawsSen > 0
  );
}
